// A note written live in place (a Case's context): the document editor in its
// "note" profile, joined to the note's live copy. Other parts put it in their
// screens; this part owns the editor and live editing.
//
// Promise:
// - Until the live copy has synced once, the stored text is drawn the way the
//   editor draws it; an empty note shows the editor at once.
// - Without a name yet (a Case not made yet) the field is written into its own
//   Y.Doc; when the name comes, the same Y.Doc joins the live copy and what was
//   written goes into it. The editor is never made again. Such a field may
//   start with markdown (`seed`: a new issue opened with a body).
// - The person's own typing stops at `maxLength` (the server cannot refuse a
//   live save). Nothing is saved from here: the live copy is.
// - A refused connection is told once through `onRefused` (the caller asks
//   for the current name and remounts with `key`, or asks for a reload).
// - With a title above (`titleRef`), Backspace / ← at the note's very start
//   and ↑ on its first line go back to the title's end.
import { useQueryClient } from "@tanstack/react-query";
import type { Editor } from "@tiptap/core";
import { Collaboration } from "@tiptap/extension-collaboration";
import { EditorContent, useEditor } from "@tiptap/react";
import {
  createEditorExtensions,
  DOCUMENT_BODY_CLASS,
  liveEditorExtensions,
  parseMarkdown,
  YDOC_FRAGMENT,
} from "@trameria/editor";
import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useMeApp } from "../app/context";
import { BlockLayers, documentBlockViews } from "./blocks";
import {
  documentEditorProps,
  documentNodeViews,
  type EditorKitContext,
  footnoteDialogExtensions,
  NoteEditorMenus,
  noteEditorExtensions,
} from "./editor/kit";
import {
  connectLiveDoc,
  createLiveDoc,
  type LiveDoc,
  type LiveTransport,
} from "./live";
import { StaticBody } from "./markdown-body";
import { focusTitleEnd, LeaveBodyToTitle } from "./title";

/** A note's look: the document body's text, without the body's tall minimum or outer margins. */
export const NOTE_CLASS = `${DOCUMENT_BODY_CLASS} text-fg select-text [&_.tiptap>:first-child]:mt-0 [&_.tiptap>:last-child]:mb-0`;

export interface LiveNoteFieldProps {
  transport: LiveTransport;
  /** The live copy's name; null while the thing does not exist yet. */
  liveName: string | null;
  /** The stored text, drawn until the live copy has synced. */
  initialMarkdown: string;
  label: string;
  placeholder: string;
  /** The longest markdown the person may type. */
  maxLength?: number;
  /** The smallest height of the field (px), so an empty note can be pressed. */
  minHeight?: number;
  onEditor?(editor: Editor | null): void;
  onRefused(name: string): void;
  /**
   * The title above the note: Backspace / ← at the note's very start and ↑ on
   * its first line go back to the title's end (as in a document).
   */
  titleRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /**
   * Markdown written into the field when it starts, for a thing not made yet
   * (no `liveName`; a new issue opened with a body). Never with a live copy.
   */
  seed?: string;
}

export function LiveNoteField(props: LiveNoteFieldProps) {
  // The Y.Doc lives as long as this field (the caller remounts it with `key`).
  const [doc, setDoc] = useState<LiveDoc | null>(null);
  const [synced, setSynced] = useState(false);
  const latest = useRef(props);
  latest.current = props;
  // frontend-development: not a fetch; owns the Y.Doc and its connection.
  useLayoutEffect(() => {
    const d = createLiveDoc();
    setDoc(d);
    return () => {
      d.destroy();
      setDoc(null);
    };
  }, []);
  const { liveName, transport } = props;
  // frontend-development: not a fetch; joins the Y.Doc to the live copy once its name is known.
  useLayoutEffect(() => {
    if (!doc || liveName === null) return;
    connectLiveDoc(doc, transport, liveName, {
      onStatus: () => {},
      onSynced: () => setSynced(true),
      onRefused: (name) => latest.current.onRefused(name),
    });
  }, [doc, liveName, transport]);
  const showsEditor = synced || props.initialMarkdown === "";
  return (
    <div className="relative" style={{ minHeight: props.minHeight }}>
      {!showsEditor && (
        <StaticBody markdown={props.initialMarkdown} className={NOTE_CLASS} />
      )}
      {doc && (
        <NoteEditor
          doc={doc}
          hidden={!showsEditor}
          label={props.label}
          placeholder={props.placeholder}
          maxLength={props.maxLength}
          minHeight={props.minHeight}
          onEditor={props.onEditor}
          titleRef={props.titleRef}
          seed={props.liveName === null ? props.seed : undefined}
        />
      )}
    </div>
  );
}

/** The editor while the static view is still shown: same place, unseen, untouchable. */
const BEHIND = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  visibility: "hidden",
  pointerEvents: "none",
} as const;

function NoteEditor({
  doc,
  hidden,
  label,
  placeholder,
  maxLength,
  minHeight,
  onEditor,
  titleRef,
  seed,
}: {
  doc: LiveDoc;
  hidden: boolean;
  label: string;
  placeholder: string;
  maxLength?: number;
  minHeight?: number;
  onEditor?(editor: Editor | null): void;
  titleRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  seed?: string;
}) {
  const app = useMeApp();
  const api = app.api;
  // Read through refs: the extensions are made once per Y.Doc.
  const placeholderRef = useRef(placeholder);
  placeholderRef.current = placeholder;
  const title = useRef(titleRef);
  title.current = titleRef;
  const kit: EditorKitContext = {
    api,
    qc: useQueryClient(),
    kinds: app.favoriteKinds,
    currentId: () => null,
  };
  const props = documentEditorProps(kit);
  const editor = useEditor(
    {
      extensions: [
        ...createEditorExtensions({
          profile: "note",
          collaboration: true,
          extensions: [...documentBlockViews({ api }), ...documentNodeViews()],
        }),
        ...liveEditorExtensions({ profile: "note", maxLength }),
        ...noteEditorExtensions(kit, {
          placeholder: () => placeholderRef.current,
        }),
        Collaboration.configure({
          document: doc.ydoc,
          fragment: doc.ydoc.getXmlFragment(YDOC_FRAGMENT),
        }),
        LeaveBodyToTitle.configure({
          onLeave: () => focusTitleEnd(title.current?.current),
        }),
      ],
      editorProps: {
        ...props,
        attributes: {
          role: "textbox",
          "aria-multiline": "true",
          "aria-label": label,
          ...(minHeight ? { style: `min-height: ${minHeight}px` } : {}),
        },
      },
      // A thing not made yet starts with what it was opened with (its own Y.Doc).
      onCreate: ({ editor: made }) => {
        if (seed?.trim() && made.isEmpty)
          made.commands.setContent(parseMarkdown(seed, "note").toJSON());
      },
      immediatelyRender: false,
    },
    [doc],
  );
  const report = useRef(onEditor);
  report.current = onEditor;
  // frontend-development: not a fetch; hands the editor to the screen (focus after a new title).
  useEffect(() => {
    report.current?.(editor);
    return () => report.current?.(null);
  }, [editor]);
  if (!editor) return null;
  return (
    <>
      <EditorContent
        editor={editor}
        // Its image blocks step together in the viewer.
        data-media-gallery=""
        className={NOTE_CLASS}
        style={hidden ? BEHIND : undefined}
        aria-hidden={hidden || undefined}
      />
      {!hidden && <NoteEditorMenus editor={editor} />}
      <BlockLayers
        editor={editor}
        media={!hidden}
        dialogExtensions={footnoteDialogExtensions}
      />
    </>
  );
}
